import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase as defaultSupabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { shareInFlight } from '@/lib/sharedRequest'
import { scopeForDomain, memberForAuthUser, type Scope } from '@/lib/scope'
import { useFamilyMembers } from '@/hooks/useFamilyMembers'
import type {
  Goal, GoalAction, GoalArea, GoalMilestone, Quarter,
  DbGoal, DbGoalAction, DbGoalArea, DbGoalMilestone,
} from '@/types/goal'

// ============================================================================
// DB → Frontend mappers
// ============================================================================

function dbAreaToArea(db: DbGoalArea): GoalArea {
  return {
    id: db.id,
    name: db.name,
    sortOrder: db.sort_order,
    createdAt: new Date(db.created_at),
  }
}

function dbMilestoneToMilestone(db: DbGoalMilestone): GoalMilestone {
  return {
    id: db.id,
    goalId: db.goal_id,
    userId: db.user_id,
    title: db.title,
    description: db.description ?? undefined,
    targetDate: db.target_date ?? undefined,
    targetValue: db.target_value ?? undefined,
    currentValue: db.current_value ?? 0,
    unit: db.unit ?? undefined,
    status: db.status,
    sortOrder: db.sort_order,
    createdAt: new Date(db.created_at),
    updatedAt: new Date(db.updated_at),
  }
}

function dbGoalToGoal(db: DbGoal, actions: GoalAction[], milestones: GoalMilestone[]): Goal {
  return {
    id: db.id,
    areaId: db.area_id,
    name: db.name,
    year: db.year,
    notes: db.notes ?? undefined,
    strategy: db.strategy ?? undefined,
    scope: db.scope ?? undefined,
    domainSlug: db.domain_slug ?? undefined,
    layerId: db.layer_id ?? undefined,
    context: db.context as Goal['context'] ?? undefined,
    status: db.status,
    sortOrder: db.sort_order,
    actions: actions.filter(a => a.goalId === db.id),
    milestones: milestones.filter(m => m.goalId === db.id),
    carriedFrom: db.carried_from ?? undefined,
    userId: db.user_id,
    // Feature-detected: only a row that HAS the column says who is assigned.
    assignedToAll: 'assigned_to_all' in db ? (db.assigned_to_all ?? []) : undefined,
    createdAt: new Date(db.created_at),
    updatedAt: new Date(db.updated_at),
  }
}

function dbActionToAction(db: DbGoalAction): GoalAction {
  return {
    id: db.id,
    goalId: db.goal_id,
    description: db.description,
    quarter: db.quarter,
    completed: db.completed,
    notes: db.notes ?? undefined,
    projectId: db.project_id ?? undefined,
    sortOrder: db.sort_order,
    createdAt: new Date(db.created_at),
  }
}

// ============================================================================
// Hook
// ============================================================================

type GoalsLoad = { areas: GoalArea[]; goals: Goal[]; actions: GoalAction[]; milestones: GoalMilestone[] }

// One trip to the database, shared by every instance that asks while it is in
// flight (the tasks hook's pattern). Prod, 2026-10-13: Month waited ~5s on
// "Loading your plans…" while each instance — the shell, the page, the detail
// panel, and again for each new auth user object — fired its own four goal
// queries, 32 requests racing the rest of startup. Keyed by the refresh round
// too, so a "plan updated" refresh never joins a load from before the write.
// No cache: a load that has finished is never reused, so writes behave as before.
function loadGoals(key: string): Promise<GoalsLoad> {
  return shareInFlight(`goals:${key}`, async (): Promise<GoalsLoad> => {
    const [areasRes, goalsRes, actionsRes, milestonesRes] = await Promise.all([
      defaultSupabase.from('goal_areas').select('*').order('sort_order'),
      defaultSupabase.from('goals').select('*').order('sort_order'),
      defaultSupabase.from('goal_actions').select('*').order('sort_order'),
      defaultSupabase.from('goal_milestones').select('*').order('sort_order'),
    ])
    if (areasRes.error) throw areasRes.error
    if (goalsRes.error) throw goalsRes.error
    if (actionsRes.error) throw actionsRes.error
    if (milestonesRes.error) throw milestonesRes.error

    const areas = (areasRes.data as DbGoalArea[]).map(dbAreaToArea)
    const mappedActions = (actionsRes.data as DbGoalAction[]).map(dbActionToAction)
    const mappedMilestones = (milestonesRes.data as DbGoalMilestone[]).map(dbMilestoneToMilestone)
    // Only the actions and milestones that belong to the fetched goals.
    const goalIds = new Set((goalsRes.data as DbGoal[]).map(g => g.id))
    const actions = mappedActions.filter(a => goalIds.has(a.goalId))
    const milestones = mappedMilestones.filter(m => goalIds.has(m.goalId))
    const goals = (goalsRes.data as DbGoal[]).map(db => dbGoalToGoal(db, actions, milestones))
    return { areas, goals, actions, milestones }
  })
}

// Every year's goals, not just this one. The year page plans NEXT year and
// looks back at the one before it, so a fetch pinned to the calendar year left
// both of those empty (final review, Phase 3). Consumers all filter by
// `g.year` themselves.
export function useGoals(supabase = defaultSupabase) {
  const { user } = useAuth()
  const { members: familyMembers } = useFamilyMembers()
  /** Only the default for a goal created without an explicit year. */
  const currentYear = new Date().getFullYear()

  const [revision,setRevision] = useState(0)
  useEffect(()=>{const refresh=()=>setRevision(v=>v+1);window.addEventListener('symphony-plan-updated',refresh);return ()=>window.removeEventListener('symphony-plan-updated',refresh)},[])
  const [areas, setAreas] = useState<GoalArea[]>([])
  const [goals, setGoals] = useState<Goal[]>([])
  const [allActions, setAllActions] = useState<GoalAction[]>([])
  const [allMilestones, setAllMilestones] = useState<GoalMilestone[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch all data on mount
  useEffect(() => {
    if (!user) {
      setAreas([])
      setGoals([])
      setAllActions([])
      setAllMilestones([])
      setLoading(false)
      return
    }

    async function fetchAll() {
      if (!user) return
      setLoading(true)
      setError(null)

      try {
        const loaded = await loadGoals(`${user.id}:${revision}`)
        setAreas(loaded.areas)
        setGoals(loaded.goals)
        setAllActions(loaded.actions)
        setAllMilestones(loaded.milestones)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch goals')
      } finally {
        setLoading(false)
      }
    }

    fetchAll()
  }, [user, revision])

  // Rebuild goals when allActions/allMilestones changes (to keep in sync)
  const goalsWithData = useMemo(() => {
    return goals.map(g => ({
      ...g,
      actions: allActions.filter(a => a.goalId === g.id),
      milestones: allMilestones.filter(m => m.goalId === g.id),
    }))
  }, [goals, allActions, allMilestones])

  // ============================================================================
  // AREA CRUD
  // ============================================================================

  const addArea = useCallback(async (name: string) => {
    if (!user) return null

    const tempId = crypto.randomUUID()
    const optimistic: GoalArea = {
      id: tempId,
      name,
      sortOrder: areas.length,
      createdAt: new Date(),
    }
    setAreas(prev => [...prev, optimistic])

    const { data, error: insertError } = await supabase
      .from('goal_areas')
      .insert({ user_id: user.id, name, sort_order: areas.length })
      .select()
      .single()

    if (insertError) {
      setAreas(prev => prev.filter(a => a.id !== tempId))
      setError(insertError.message)
      return null
    }

    const real = dbAreaToArea(data as DbGoalArea)
    setAreas(prev => prev.map(a => a.id === tempId ? real : a))
    return real
  }, [user, areas.length])

  const updateArea = useCallback(async (id: string, updates: { name?: string; sortOrder?: number }) => {
    const area = areas.find(a => a.id === id)
    if (!area) return

    setAreas(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a))

    const dbUpdates: Record<string, unknown> = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder

    const { error: updateError } = await supabase
      .from('goal_areas')
      .update(dbUpdates)
      .eq('id', id)

    if (updateError) {
      setAreas(prev => prev.map(a => a.id === id ? area : a))
      setError(updateError.message)
    }
  }, [areas])

  const deleteArea = useCallback(async (id: string) => {
    const areaToDelete = areas.find(a => a.id === id)
    if (!areaToDelete) return

    // Also remove goals in this area
    const goalsInArea = goals.filter(g => g.areaId === id)
    const goalIdsInArea = new Set(goalsInArea.map(g => g.id))

    setAreas(prev => prev.filter(a => a.id !== id))
    setGoals(prev => prev.filter(g => g.areaId !== id))
    setAllActions(prev => prev.filter(a => !goalIdsInArea.has(a.goalId)))

    const { error: deleteError } = await supabase
      .from('goal_areas')
      .delete()
      .eq('id', id)

    if (deleteError) {
      setAreas(prev => [...prev, areaToDelete])
      setGoals(prev => [...prev, ...goalsInArea])
      setError(deleteError.message)
    }
  }, [areas, goals])

  // ============================================================================
  // GOAL CRUD
  // ============================================================================

  const addGoal = useCallback(async (
    areaId: string | null,
    name: string,
    context?: 'work' | 'family' | 'personal',
    extra?: { notes?: string | null; scope?: Scope; id?: string; year?: number; strategy?: string | null; carriedFrom?: string | null; assignedToAll?: string[]; sortOrder?: number },
  ) => {
    if (!user) return null

    const tempId = extra?.id ?? crypto.randomUUID()
    const goalYear = extra?.year ?? currentYear
    const optimistic: Goal = {
      id: tempId,
      areaId: areaId ?? '',
      name,
      year: goalYear,
      notes: extra?.notes ?? undefined,
      strategy: extra?.strategy ?? undefined,
      context: context ?? undefined,
      status: 'active',
      // A caller that orders its list passes its own place (the year page puts
      // a new goal last); otherwise the count of goals in that area.
      sortOrder: extra?.sortOrder ?? goals.filter(g => g.areaId === areaId).length,
      actions: [],
      milestones: [],
      carriedFrom: extra?.carriedFrom ?? undefined,
      ...(extra?.assignedToAll ? { assignedToAll: extra.assignedToAll } : {}),
      createdAt: new Date(),
      updatedAt: new Date(),
    }
    setGoals(prev => [...prev, optimistic])

    const { data, error: insertError } = await supabase
      .from('goals')
      .insert({
        ...(extra?.id ? { id: extra.id } : {}),
        user_id: user.id,
        area_id: areaId,
        name,
        year: goalYear,
        sort_order: optimistic.sortOrder,
        context: context ?? null,
        notes: extra?.notes ?? null,
        strategy: extra?.strategy ?? null,
        carried_from: extra?.carriedFrom ?? null,
        // The people, as updateGoal writes them: an empty list is stored as
        // null (Unassigned). Only sent when the caller chose — see updateGoal.
        ...(extra?.assignedToAll ? { assigned_to_all: extra.assignedToAll.length ? extra.assignedToAll : null } : {}),
        // goals RLS shares on scope, not context — a family goal without this
        // stays private to its author despite looking shared. It is DERIVED,
        // never chosen (scope.ts).
        scope: extra?.scope ?? scopeForDomain(context ?? null, [], null),
      })
      .select()
      .single()

    if (insertError) {
      // Idempotent create: a duplicate on a caller-given id means another
      // attempt already landed the row — read it back rather than failing
      // (mirrors addTask({ id }), useSupabaseTasks.ts).
      if (extra?.id && (insertError as { code?: string }).code === '23505') {
        setGoals(prev => prev.filter(g => g.id !== tempId))
        const { data: existing } = await supabase
          .from('goals')
          .select()
          .eq('id', extra.id)
          .single()
        if (existing) {
          const real = dbGoalToGoal(existing as DbGoal, [], [])
          setGoals(prev => prev.some(g => g.id === real.id) ? prev : [...prev, real])
          return real
        }
      }
      setGoals(prev => prev.filter(g => g.id !== tempId))
      setError(insertError.message)
      return null
    }

    const real = dbGoalToGoal(data as DbGoal, [], [])
    setGoals(prev => prev.map(g => g.id === tempId ? real : g))
    return real
  }, [user, currentYear, goals])

  // `quiet`: a refused write rolls back and answers false without raising the
  // hook's error (which a page reads as "could not load") — for writes the
  // caller reports itself, such as a reorder that rolls its siblings back.
  const updateGoal = useCallback(async (id: string, rawUpdates: Partial<Pick<Goal, 'name' | 'notes' | 'status' | 'areaId' | 'sortOrder' | 'strategy' | 'domainSlug' | 'layerId' | 'context' | 'year' | 'carriedFrom' | 'assignedToAll'>>, opts?: { quiet?: boolean }) => {
    const goal = goals.find(g => g.id === id)
    if (!goal) return
    let updates: Partial<Goal> = rawUpdates
    // Assignment shares, never relabels — the task rule (updateTask): someone
    // else assigned raises scope to 'couple' so they can read it; no one else
    // assigned walks it back; a family goal stays household-wide. The context
    // is never touched. A goal from before the column has no assignees to
    // write, so the call is ignored rather than failing.
    if ('assignedToAll' in rawUpdates) {
      if (goal.assignedToAll === undefined) return
      const ids = rawUpdates.assignedToAll ?? []
      const self = memberForAuthUser(familyMembers, goal.userId)?.id
      updates = { ...rawUpdates, assignedToAll: ids, scope: scopeForDomain(goal.context ?? null, ids, self) }
    }
    // A new life area moves who may read it, the same way: scope is derived
    // from the domain and the assignees, so re-tagging a Family goal Personal
    // takes it out of the household's view (it kept its old scope before).
    if ('context' in rawUpdates) {
      const ids = updates.assignedToAll ?? goal.assignedToAll ?? []
      const self = memberForAuthUser(familyMembers, goal.userId)?.id
      updates = { ...updates, scope: scopeForDomain(rawUpdates.context ?? null, ids, self) }
    }

    setGoals(prev => prev.map(g => g.id === id ? { ...g, ...updates } : g))

    const dbUpdates: Record<string, unknown> = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes ?? null
    if (updates.status !== undefined) dbUpdates.status = updates.status
    if (updates.areaId !== undefined) dbUpdates.area_id = updates.areaId
    if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder
    if (updates.strategy !== undefined) dbUpdates.strategy = updates.strategy ?? null
    if (updates.domainSlug !== undefined) dbUpdates.domain_slug = updates.domainSlug ?? null
    if (updates.layerId !== undefined) dbUpdates.layer_id = updates.layerId ?? null
    if (updates.context !== undefined) dbUpdates.context = updates.context ?? null
    if (updates.year !== undefined) dbUpdates.year = updates.year
    if (updates.carriedFrom !== undefined) dbUpdates.carried_from = updates.carriedFrom ?? null
    if (updates.assignedToAll !== undefined) dbUpdates.assigned_to_all = updates.assignedToAll.length > 0 ? updates.assignedToAll : null
    if (updates.scope !== undefined) dbUpdates.scope = updates.scope

    const { error: updateError } = await supabase
      .from('goals')
      .update(dbUpdates)
      .eq('id', id)

    if (updateError) {
      setGoals(prev => prev.map(g => g.id === id ? goal : g))
      if (!opts?.quiet) setError(updateError.message)
      // Refused writes are reported, so a surface can say why (a partner
      // making the owner's shared goal private is refused by RLS).
      return false
    }
    return true
  }, [goals, familyMembers])

  const deleteGoal = useCallback(async (id: string) => {
    const goalToDelete = goals.find(g => g.id === id)
    if (!goalToDelete) return

    const actionsToDelete = allActions.filter(a => a.goalId === id)

    setGoals(prev => prev.filter(g => g.id !== id))
    setAllActions(prev => prev.filter(a => a.goalId !== id))

    const { error: deleteError } = await supabase
      .from('goals')
      .delete()
      .eq('id', id)

    if (deleteError) {
      setGoals(prev => [...prev, goalToDelete])
      setAllActions(prev => [...prev, ...actionsToDelete])
      setError(deleteError.message)
    }
  }, [goals, allActions])

  // ============================================================================
  // ACTION CRUD
  // ============================================================================

  const addAction = useCallback(async (goalId: string, description: string, quarter: Quarter) => {
    const tempId = crypto.randomUUID()
    const existing = allActions.filter(a => a.goalId === goalId && a.quarter === quarter)
    const optimistic: GoalAction = {
      id: tempId,
      goalId,
      description,
      quarter,
      completed: false,
      sortOrder: existing.length,
      createdAt: new Date(),
    }
    setAllActions(prev => [...prev, optimistic])

    const { data, error: insertError } = await supabase
      .from('goal_actions')
      .insert({
        goal_id: goalId,
        description,
        quarter,
        sort_order: optimistic.sortOrder,
      })
      .select()
      .single()

    if (insertError) {
      setAllActions(prev => prev.filter(a => a.id !== tempId))
      setError(insertError.message)
      return null
    }

    const real = dbActionToAction(data as DbGoalAction)
    setAllActions(prev => prev.map(a => a.id === tempId ? real : a))
    return real
  }, [allActions])

  const updateAction = useCallback(async (id: string, updates: Partial<Pick<GoalAction, 'description' | 'completed' | 'notes' | 'sortOrder'>>) => {
    const action = allActions.find(a => a.id === id)
    if (!action) return

    setAllActions(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a))

    const dbUpdates: Record<string, unknown> = {}
    if (updates.description !== undefined) dbUpdates.description = updates.description
    if (updates.completed !== undefined) dbUpdates.completed = updates.completed
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes ?? null
    if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder

    const { error: updateError } = await supabase
      .from('goal_actions')
      .update(dbUpdates)
      .eq('id', id)

    if (updateError) {
      setAllActions(prev => prev.map(a => a.id === id ? action : a))
      setError(updateError.message)
    }
  }, [allActions])

  const toggleAction = useCallback(async (id: string) => {
    const action = allActions.find(a => a.id === id)
    if (!action) return
    await updateAction(id, { completed: !action.completed })
  }, [allActions, updateAction])

  const deleteAction = useCallback(async (id: string) => {
    const actionToDelete = allActions.find(a => a.id === id)
    if (!actionToDelete) return

    setAllActions(prev => prev.filter(a => a.id !== id))

    const { error: deleteError } = await supabase
      .from('goal_actions')
      .delete()
      .eq('id', id)

    if (deleteError) {
      setAllActions(prev => [...prev, actionToDelete])
      setError(deleteError.message)
    }
  }, [allActions])

  // ============================================================================
  // Computed helpers
  // ============================================================================

  const getGoalsByArea = useCallback((areaId: string) => {
    return goalsWithData.filter(g => g.areaId === areaId)
  }, [goalsWithData])

  const getGoalById = useCallback((id: string) => {
    return goalsWithData.find(g => g.id === id)
  }, [goalsWithData])

  const getCurrentQuarter = useCallback((): Quarter => {
    const month = new Date().getMonth() // 0-indexed
    if (month < 3) return 'Q1'
    if (month < 6) return 'Q2'
    if (month < 9) return 'Q3'
    return 'Q4'
  }, [])

  // Allow external updates to milestones. UI-facing exports; the Goals app
  // UI no longer creates/edits actions or milestones (Task 13), but this
  // plumbing and the underlying DB tables stay intact.
  const addMilestoneLocal = useCallback((milestone: GoalMilestone) => {
    setAllMilestones(prev => [...prev, milestone])
  }, [])

  const updateMilestoneLocal = useCallback((id: string, updates: Partial<GoalMilestone>) => {
    setAllMilestones(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m))
  }, [])

  const removeMilestoneLocal = useCallback((id: string) => {
    setAllMilestones(prev => prev.filter(m => m.id !== id))
  }, [])

  return {
    areas,
    goals: goalsWithData,
    loading,
    error,
    // Area CRUD
    addArea,
    updateArea,
    deleteArea,
    // Goal CRUD
    addGoal,
    updateGoal,
    deleteGoal,
    // Action CRUD
    addAction,
    updateAction,
    toggleAction,
    deleteAction,
    // Milestone state helpers
    addMilestoneLocal,
    updateMilestoneLocal,
    removeMilestoneLocal,
    // Helpers
    getGoalsByArea,
    getGoalById,
    getCurrentQuarter,
  }
}
